import React, { useState, useEffect } from 'react';
import { DataTable, Badge, Button, Avatar, Pagination } from "@/components/ui";
import { Package, Eye, Edit, Trash2, ChevronRight, TrendingUp, AlertTriangle, Tag, Boxes, Plus } from 'lucide-react';
import { fmt } from '@/lib/formatters';

/**
 * InventoryTable Component
 * Refactored to match the high-end design of the Leads module.
 * Uses the standard DataTable component with premium cell rendering.
 */
export const InventoryTable = ({ 
  filtered, 
  activeTab, 
  onEditClick, 
  onViewClick, 
  onDeleteClick,
  onRestockClick,
  distributedMap = {},
  purchasedMap = {}
}) => {
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setPage(1);
  }, [filtered]);

  const columns = [
    {
      header: "Item Name",
      className: "min-w-[200px]",
      render: (record) => {
        const item = record.item || {};
        const name = item.itemName || item.name || record.name || 'Unknown';
        const currentQty = Number(record.availableQty || 0);
        const isLow = currentQty <= (record.minStock || 0);
        const isOut = currentQty <= 0;
        return (
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${isOut ? 'bg-rose-500' : isLow ? 'bg-amber-500' : 'bg-emerald-500'}`} />
            <span className="text-[13px] font-normal text-slate-700 font-['Poppins'] truncate">
              {name}
            </span>
          </div>
        );
      }
    },
    {
      header: "Category",
      render: (record) => {
        const item = record.item || {};
        const catObj = item.category || record.category;
        const label = typeof catObj === 'object' && catObj !== null 
          ? (catObj.categoryName || catObj.title || catObj.name) 
          : (catObj || record.categoryName || 'N/A');
        
        return (
          <span className="text-[12px] font-normal text-slate-600 font-['Poppins']">
            {String(label || '—')}
          </span>
        );
      }
    },
    {
      header: "Total Stock",
      className: "text-center",
      render: (record) => {
        const qty = Number(record._stock || 0);
        return (
          <div className="flex flex-col items-center">
            <span className="text-[13px] font-normal text-slate-700 font-['Poppins']">{qty}</span>
            <span className="text-[10px] font-normal text-slate-400 uppercase tracking-tight">Total</span>
          </div>
        );
      }
    },
    {
      header: "Available",
      render: (record) => {
        const available = Number(record.availableQty || 0);
        const total = Number(record._stock || 1);
        const minQty = Number(record.minQty || record.minStock || 0);
        const isLow = available <= minQty && minQty > 0;
        const isOut = available <= 0;
        
        const item = record.item || {};
        const unitObj = item.units || record.units;
        const unitLabel = typeof unitObj === 'object' && unitObj !== null 
          ? (unitObj.unitName || unitObj.title || unitObj.name) 
          : (unitObj || record.unit || 'nos');
        
        return (
          <div className="flex flex-col gap-1.5 min-w-[100px]">
            <div className="flex items-center gap-1.5">
               <span className={`text-[13px] font-normal font-['Poppins'] ${isOut ? 'text-rose-600' : isLow ? 'text-amber-600' : 'text-emerald-600'}`}>
                {available}
              </span>
              <span className="text-[10px] font-normal text-slate-400 uppercase">{String(unitLabel)}</span>
            </div>
            <div className="w-full bg-slate-100 h-1 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-700 rounded-full ${isOut ? 'bg-rose-500' : isLow ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${Math.min(100, (available / total) * 100)}%` }}
              />
            </div>
          </div>
        );
      }
    },
    {
      header: "Sale",
      className: "text-center",
      render: (record) => {
        const total = Number(record._stock || 0);
        const available = Number(record.availableQty || 0);
        const sold = Math.max(0, total - available);
        const item = record.item || {};
        const unitObj = item.units || record.units;
        const unitLabel = typeof unitObj === 'object' && unitObj !== null
          ? (unitObj.unitName || unitObj.title || unitObj.name)
          : (unitObj || record.unit || 'nos');
        return (
          <div className="flex flex-col items-center">
            <span className="text-[13px] font-normal text-slate-600 font-['Poppins']">{sold}</span>
            <span className="text-[10px] font-normal text-slate-400 uppercase tracking-tight">{String(unitLabel)}</span>
          </div>
        );
      }
    },
    {
      header: "Min Stock",
      className: "text-center",
      render: (record) => {
        const min = Number(record.minQty || record.minStock || 0);
        const available = Number(record.availableQty || 0);
        const isLow = available <= min && min > 0;
        return (
          <div className="flex flex-col items-center">
            <span className={`text-[13px] font-normal font-['Poppins'] ${isLow ? 'text-amber-600' : 'text-slate-600'}`}>
              {min}
            </span>
            {isLow && (
              <span className="text-[9px] text-amber-500 uppercase tracking-tight">low</span>
            )}
          </div>
        );
      }
    },
    {
      header: "Total Cost",
      className: "text-right",
      render: (record) => {
        // totalCost = accumulated sum across all purchases/openstock (never overwritten, always $inc)
        const totalCost = Number(record.totalCost || record.totalPurchaseAmount || 0);
        return (
          <span className="text-[13px] font-normal text-slate-700 font-['Poppins']">
            {totalCost > 0 ? `₹${fmt(totalCost)}` : "—"}
          </span>
        );
      }
    },
    {
      header: "Status",
      className: "text-center",
      render: (record) => {
        const available = Number(record.availableQty || 0);
        const isLow = available <= (record.minStock || 0);
        const isOut = available <= 0;
        
        if (isOut) return <span className="px-2 py-0.5 rounded text-[10px] font-normal uppercase border bg-rose-50 text-rose-600 border-rose-100">Out of Stock</span>;
        if (isLow) return <span className="px-2 py-0.5 rounded text-[10px] font-normal uppercase border bg-amber-50 text-amber-600 border-amber-100">Low Stock</span>;
        return <span className="px-2 py-0.5 rounded text-[10px] font-normal uppercase border bg-emerald-50 text-emerald-600 border-emerald-100">In Stock</span>;
      }
    },
    {
      header: "Actions",
      className: "text-center w-16",
      render: (item) => (
        <div className="flex items-center justify-center gap-1">
          <Button 
            variant="ghost" 
            size="icon-xs" 
            onClick={() => onViewClick(item)} 
            className="text-slate-300 hover:text-[#2b448f] hover:bg-slate-50 transition-all active:scale-90"
            title="View Details"
          >
            <Eye size={15} />
          </Button>
        </div>
      )
    }
  ];

  const currentData = (filtered || []).slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const totalPages = Math.max(Math.ceil((filtered || []).length / itemsPerPage), 1);

  return (
    <div className="animate-in fade-in duration-500 py-6">
      <DataTable 
        columns={columns} 
        data={currentData} 
        className="border-none shadow-none rounded-none"
        getRowClassName={(idx) => idx % 2 === 0 ? "bg-white" : "bg-slate-50/20"}
      />
      
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </div>
  );
};
